import { useQuery } from "@tanstack/react-query"
import axios from "axios"

import { TicketGroupsResponse } from "types"

interface UseTicketGroupsOptions {
  enabled?: boolean
}

/**
 * Custom hook for fetching ticket groups associated with a registration bundle.
 *
 * Ticket groups are organizational units that help categorize different types
 * of tickets within a registration system. For example, you might have groups
 * like "General Admission", "VIP Access", "Student Tickets", etc.
 *
 * Features:
 * - React Query integration for caching and background refetching
 * - Automatic error handling with console logging
 * - Optimistic caching (5 minutes stale time)
 * - Conditional querying based on bundleUUID availability
 * - TypeScript type safety
 *
 * @param bundleUUID - The unique identifier for the registration bundle
 * @param options - Additional configuration options for the query
 * @returns React Query result object containing:
 *   - data: TicketGroupsResponse with available ticket groups
 *   - isLoading: Boolean indicating if the initial request is in progress
 *   - error: Error object if the request failed
 *   - refetch: Function to manually trigger a refetch
 *
 * @example
 * ```tsx
 * const { data: ticketGroups, isLoading, error } = useTicketGroupsData(bundleUUID)
 *
 * if (isLoading) return <div>Loading ticket groups...</div>
 * if (error) return <div>Error loading ticket groups</div>
 *
 * return (
 *   <div>
 *     {ticketGroups?.results?.map(group => (
 *       <TicketGroupCard key={group.id} group={group} />
 *     ))}
 *   </div>
 * )
 * ```
 */
export const useTicketGroupsData = (
  bundleUUID: string | undefined,
  options?: UseTicketGroupsOptions
) => {
  return useQuery<TicketGroupsResponse>({
    queryKey: ["ticket-groups", bundleUUID],
    queryFn: async () => {
      try {
        const response = await axios.get<TicketGroupsResponse>(
          `/api/registration/ticket-group-published/?bundle__uuid=${bundleUUID}`
        )
        return response.data
      } catch (error) {
        console.error("Error fetching ticket groups:", error)
        throw error
      }
    },
    enabled: !!bundleUUID && options?.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}
